import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { LabelLayout } from '../../../../core/models/label-layout.model';
import { SheetFormat } from '../../../../core/models/sheet-format.model';
import { LabelPreviewComponent } from '../label-preview/label-preview.component';

/**
 * Interactive grid representing a label sheet.
 * Each cell is clickable to toggle selection.
 * Grid columns/rows match the sheet format configuration.
 */
@Component({
  selector: 'app-sheet-grid',
  imports: [LabelPreviewComponent],
  templateUrl: './sheet-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sheet-grid.component.css',
})
export class SheetGridComponent {
  @Input() sheetFormat!: SheetFormat;
  @Input() labeledPositions: Set<number> = new Set();
  @Input() usedPositions: Set<number> = new Set();
  @Input() labelLayout: LabelLayout | null = null;
  @Input() checkpointName = '';
  @Input() formName = '';
  @Input() showCheckpointName = true;
  @Input() showFormName = true;
  @Input() qrUrl = '';
  @Input() checkpointNameFontSize = 10;
  @Input() formNameFontSize = 8;
  @Input() fontFamily = 'helvetica';
  @Input() debug = false;
  @Input() showCode = true;
  @Input() qrX?: number;
  @Input() qrY?: number;
  @Input() qrWidth?: number;
  @Input() qrHeight?: number;
  @Input() checkpointNameX?: number;
  @Input() checkpointNameY?: number;
  @Input() checkpointNameWidth?: number;
  @Input() checkpointNameHeight?: number;
  @Input() formNameX?: number;
  @Input() formNameY?: number;
  @Input() formNameWidth?: number;
  @Input() formNameHeight?: number;
  @Input() showSerialNumber = true;
  @Input() serialNumberFontSize = 8;
  @Input() serialNumberX?: number;
  @Input() serialNumberY?: number;
  @Input() serialNumberWidth?: number;
  @Input() serialNumberHeight?: number;
  /** Code type: 'qr' (default) or 'barcode' */
  @Input() codeType: 'qr' | 'barcode' = 'qr';
  /** Map of position index → serial number string for instanced checkpoints */
  @Input() positionSerials: Map<number, string> = new Map();
  @Output() positionToggled = new EventEmitter<number>();
  @Output() positionRightClicked = new EventEmitter<number>();

  get gridTemplateColumns(): string {
    if (!this.sheetFormat) return '1fr';
    return this.buildTrackTemplate(
      this.sheetFormat.columns,
      this.sheetFormat.label_width,
      this.sheetFormat.gutter_horizontal,
      this.sheetFormat.margin_left,
      this.rightMarginMm,
    );
  }

  get gridTemplateRows(): string {
    if (!this.sheetFormat) return '1fr';
    return this.buildTrackTemplate(
      this.sheetFormat.rows,
      this.sheetFormat.label_height,
      this.sheetFormat.gutter_vertical,
      this.sheetFormat.margin_top,
      this.bottomMarginMm,
    );
  }

  get sheetAspectRatio(): string {
    if (!this.sheetFormat) return '1 / 1';
    return `${this.sheetFormat.page_width} / ${this.sheetFormat.page_height}`;
  }

  get positions(): number[] {
    if (!this.sheetFormat) return [];
    const total = this.sheetFormat.columns * this.sheetFormat.rows;
    return Array.from({ length: total }, (_, i) => i);
  }

  get rowIndices(): number[] {
    if (!this.sheetFormat) return [];
    return Array.from({ length: this.sheetFormat.rows }, (_, i) => i);
  }

  getGridColumn(position: number): string {
    if (!this.sheetFormat) return '1 / span 1';
    const col = position % this.sheetFormat.columns;
    const start = 2 + col * 2;
    return `${start} / span 1`;
  }

  getGridRow(position: number): string {
    if (!this.sheetFormat) return '1 / span 1';
    const row = Math.floor(position / this.sheetFormat.columns);
    const start = 2 + row * 2;
    return `${start} / span 1`;
  }

  getRowNumberGridRow(rowIndex: number): string {
    const start = 2 + rowIndex * 2;
    return `${start} / span 1`;
  }

  isLabeled(position: number): boolean {
    return this.labeledPositions.has(position);
  }

  isUsed(position: number): boolean {
    return this.usedPositions.has(position);
  }

  onCellClick(position: number): void {
    this.positionToggled.emit(position);
  }

  onCellRightClick(event: MouseEvent, position: number): void {
    event.preventDefault();
    this.positionRightClicked.emit(position);
  }

  onKeydown(event: KeyboardEvent, position: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.positionToggled.emit(position);
    }
  }

  private get rightMarginMm(): number {
    const fmt = this.sheetFormat;
    if (!fmt) return 0;
    const usedWidth =
      fmt.margin_left + fmt.columns * fmt.label_width + (fmt.columns - 1) * fmt.gutter_horizontal;
    return this.clampMm(fmt.page_width - usedWidth);
  }

  private get bottomMarginMm(): number {
    const fmt = this.sheetFormat;
    if (!fmt) return 0;
    const usedHeight =
      fmt.margin_top + fmt.rows * fmt.label_height + (fmt.rows - 1) * fmt.gutter_vertical;
    return this.clampMm(fmt.page_height - usedHeight);
  }

  private buildTrackTemplate(
    count: number,
    labelSizeMm: number,
    gutterMm: number,
    leadingMarginMm: number,
    trailingMarginMm: number,
  ): string {
    if (count <= 0) return '1fr';

    const tracks: string[] = [`${this.clampMm(leadingMarginMm)}fr`];
    for (let i = 0; i < count; i++) {
      tracks.push(`${this.clampMm(labelSizeMm)}fr`);
      if (i < count - 1) {
        tracks.push(`${this.clampMm(gutterMm)}fr`);
      }
    }
    tracks.push(`${this.clampMm(trailingMarginMm)}fr`);
    return tracks.join(' ');
  }

  private clampMm(value: number): number {
    if (!Number.isFinite(value) || value < 0) return 0;
    return Number(value.toFixed(4));
  }
}
